import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTable, usePagination, useRowSelect } from 'react-table'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { Checkbox } from '../../components'

/**
 * Table Component
 */
const Table = ({ data, columns, withCheckbox, withPagination, noHeader }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,

    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data
    },
    usePagination,
    useRowSelect,
    hooks => {
      // If withCheckbox prop is true, append the checkbox to the table
      if (withCheckbox) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            // Check All Checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <Checkbox {...getToggleAllRowsSelectedProps()} />
            ),
            // Row check
            Cell: ({ row }) => (
              <Checkbox {...row.getToggleRowSelectedProps()} />
            )
          },
          ...columns,
        ])
      }
    }
  )

  // Pagination
  const endSize = 1
  const midSize = 1
  const currentPage = pageIndex + 1
  const paginationNumbers = useMemo(
    () => {
      const numbers = []
      let dots = false

      for (let index = 1; index <= pageCount; index++) {
        if (index === currentPage) {
          numbers.push(index)
          dots = true
        } else {
          if (index <= endSize || (currentPage && index >= currentPage - midSize && index <= currentPage + midSize) || index > pageCount - endSize ) {
            numbers.push(index)
            dots = true
          } else if (dots) {
            numbers.push('elipsis')
            dots = false
          }
        }
      }

      return numbers
    },
    [currentPage, pageCount]
  )

  return (
    <div>
      <table className="table table--striped" {...getTableProps()}>
        {!noHeader && (
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} className="text-xs">
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()} className="align-top">
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}

        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()} className="text-sm text-gray-444 shadow-on-hover">
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      { /* Pagination */ }
      {withPagination && (
        <div className="flex justify-center text-gray-999 mt-8 lg:mt-16 mb-18">
          <button className="mx-3" onClick={() => previousPage()} disabled={!canPreviousPage}>
            <ChevronLeft/>
          </button>

          {paginationNumbers.map((number, index) => {
            if (number === 'elipsis') {
              return (
                <div className="mx-3" key={index}>
                  &hellip;
                </div>
              )
            } else {
              return (
                <button
                  className={`mx-3 ${number === currentPage ? 'text-primary' : ''}`}
                  onClick={() => gotoPage(number - 1)}
                  key={index}
                >
                  {number}
                </button>
              )
            }
          })}

          <button className="mx-3" onClick={() => nextPage()} disabled={!canNextPage}>
            <ChevronRight/>
          </button>
        </div>
      )}
    </div>
  )
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  withCheckbox: PropTypes.bool,
  noHeader: PropTypes.bool,
  withPagination: PropTypes.bool,
}

Table.defaultProps = {
  withCheckbox: false,
  noHeader: false,
  withPagination: true,
}

export { Table }
